import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { isBefore } from 'date-fns';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, map, startWith, takeUntil, withLatestFrom } from 'rxjs/operators';

import { AsyncPipe, NgIf, NgTemplateOutlet } from '@angular/common';
import { DroppanelStyle } from '../../../../elements/components/droppanel/entities/droppanel-style.entity';
import { ShFormInputWithDroppanelComponent as ShFormInputWithDroppanelComponent_1 } from '../../../../form-controls/components/_core/form-input-with-droppanel/form-input-with-droppanel.component';
import { ShFormInputWithDroppanelComponent } from '../../../../form-controls/components/_core/form-input-with-droppanel/form-input-with-droppanel.module';
import { ShFormDateSelectComponent } from '../../../../form-controls/components/form-date-select/form-date-select.component';
import { FormInputWithDroppanelVariant } from '../../../../form-controls/entities/form-input-with-droppanel-variant.entity';
import { MakeProvider } from '../../../../helpers/base-control-value-accessor';
import { BaseFilterDirective } from '../../base/components/base-filter/base-filter.component';
import { FilterDateTranslations, FilterDateTranslationsPartial } from '../../base/entities/filter-date-translations';
import { FilterDateType } from '../../base/entities/filter-date-type.entity';
import { FilterDateValue } from '../../base/entities/filter-date-value.entity';
import { FilterDate } from '../../base/entities/filter-date.entity';
import { DateFilter } from './filter-date.class';

@Component({
    selector: 'sh-filter-date',
    templateUrl: './filter-date.component.html',
    styleUrls: ['./filter-date.component.scss'],
    providers: [MakeProvider(ShFilterDateComponent)],
    standalone: true,
    imports: [
        ShFormInputWithDroppanelComponent_1,
        ReactiveFormsModule,
        ShFormDateSelectComponent,
        NgIf,
        NgTemplateOutlet,
        AsyncPipe
    ]
})
export class ShFilterDateComponent extends BaseFilterDirective<DateFilter> implements OnInit {
    private readonly updateLabel$: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(
        undefined
    );

    private readonly setFilterValues$: Subject<void> = new Subject();

    private readonly defaultTranslations: FilterDateTranslations = {
        year: 'Year',
        month: 'Month',
        day: 'Day',
        range: 'Period',
        from: 'From',
        until: 'Until',
        specific: 'Specific date',
        submit: 'Submit',
        invalidRange: 'Invalid date range. Startdate needs to be an earlier date than the enddate.',
        incompleteDate: '1 or more dates are incomplete.'
    };

    public readonly filterDateType: typeof FilterDateType = FilterDateType;

    public id$!: Observable<string>;
    public label$!: Observable<string>;
    public variant$!: Observable<FormInputWithDroppanelVariant>;
    public droppanelStyle$!: Observable<DroppanelStyle>;
    public canSetFilterValues$!: Observable<boolean>;
    public error$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    public form!: UntypedFormGroup;
    public filterLabels: FilterDateTranslations = this.defaultTranslations;

    @Input() set i18n(customTranslations: FilterDateTranslationsPartial) {
        this.filterLabels = {
            ...this.defaultTranslations,
            ...customTranslations
        };
    }

    @ViewChild(ShFormInputWithDroppanelComponent) inputWithDroppanelComponent!: ShFormInputWithDroppanelComponent;

    constructor(private readonly formBuilder: UntypedFormBuilder) {
        super();
    }

    ngOnInit(): void {
        this.initForm();

        this.initDataStreams();
        this.initDefaultValuesListener();

        this.watchTypeSelection();
        this.watchFormValidation();
        this.watchSetFilterValues();
    }

    public setFilterValues(): void {
        this.setFilterValues$.next();
    }

    private initDataStreams(): void {
        this.initFilterDataStream();
    }

    private initFilterDataStream(): void {
        this.id$ = this.pureFilterStream$.pipe(map(pureFilter => `${pureFilter.field}-filter-date`));
        this.label$ = combineLatest([this.pureFilterStream$, this.updateLabel$]).pipe(
            map(([pureFilter, labelValue]: [DateFilter, string | undefined]) => {
                const valueSuffix: string = labelValue ? `: ${labelValue}` : '';
                return `${pureFilter.label}${valueSuffix}`;
            })
        );
        this.variant$ = this.pureFilterStream$.pipe(map(pureFilter => pureFilter.variant));
        this.droppanelStyle$ = this.pureFilterStream$.pipe(map(pureFilter => pureFilter.droppanelStyle));
    }

    private initForm(): void {
        this.form = this.formBuilder.group(
            {
                Type: [this.filterDateType.RANGE],
                Range: this.formBuilder.group({
                    From: this.formBuilder.group({
                        Year: [null],
                        Month: [null],
                        Day: [null]
                    }),
                    To: this.formBuilder.group({
                        Year: [null],
                        Month: [null],
                        Day: [null]
                    })
                }),
                Specific: this.formBuilder.group({
                    Year: [null],
                    Month: [null],
                    Day: [null]
                })
            },
            { validator: this.dateValidation.bind(this) }
        );
    }

    private watchTypeSelection(): void {
        this.filterTypeControl.valueChanges
            .pipe(startWith(this.filterTypeControl.value), takeUntil(this.destroy$))
            .subscribe((typeValue: FilterDateType) =>
                typeValue === FilterDateType.RANGE ? this.setRangeTypeActive() : this.setSpecificTypeActive()
            );
    }

    private setRangeTypeActive(): void {
        this.filterRangeControl.enable();
        this.filterSpecificControl.disable();
    }

    private setSpecificTypeActive(): void {
        this.filterRangeControl.disable();
        this.filterSpecificControl.enable();
    }

    private watchFormValidation(): void {
        this.canSetFilterValues$ = this.form.valueChanges.pipe(
            startWith(this.form.value),
            debounceTime(10),
            map(() => this.form.valid)
        );
    }

    private initDefaultValuesListener(): void {
        this.pureFilterStream$.pipe(takeUntil(this.destroy$)).subscribe((dateFilter: DateFilter) => {
            const queryPartString: string =
                dateFilter && dateFilter.values && dateFilter.values[0] ? dateFilter.values[0] : '';

            this.setFormValuesWithQueryPartString(queryPartString);
        });
    }

    private setFormValuesWithQueryPartString(queryPartString: string): void {
        const filterDate: FilterDate = FilterDate.fromQueryPartString(queryPartString);
        // if no date, return
        this.filterTypeControl.setValue(filterDate.filterDateType);

        filterDate.filterDateType === FilterDateType.RANGE
            ? this.setRangeValues(filterDate)
            : this.setSpecificValues(filterDate);

        this.updateLabel$.next(this.getTypeLabelFromFilterDate(filterDate));
    }

    private setRangeValues(filterDate: FilterDate): void {
        this.filterRangeFromYearControl.setValue(filterDate.value.Range?.From?.Year);
        this.filterRangeFromMonthControl.setValue(filterDate.value.Range?.From?.Month);
        this.filterRangeFromDayControl.setValue(filterDate.value.Range?.From?.Day);
        this.filterRangeToYearControl.setValue(filterDate.value.Range?.To?.Year);
        this.filterRangeToMonthControl.setValue(filterDate.value.Range?.To?.Month);
        this.filterRangeToDayControl.setValue(filterDate.value.Range?.To?.Day);
    }

    private setSpecificValues(filterDate: FilterDate): void {
        this.filterSpecificYearControl.setValue(filterDate.value.Specific?.Year);
        this.filterSpecificMonthControl.setValue(filterDate.value.Specific?.Month);
        this.filterSpecificDayControl.setValue(filterDate.value.Specific?.Day);
    }

    private watchSetFilterValues(): void {
        this.setFilterValues$
            .pipe(withLatestFrom(this.pureFilterStream$), takeUntil(this.destroy$))
            .subscribe(([_, dateFilter]) => {
                const filterDateValue: FilterDateValue = this.getFilterDateValueFromForm();
                const filterDate: FilterDate = new FilterDate(filterDateValue);
                const filterDateQueryParamsString: string = filterDate.queryPartString;

                dateFilter.values = [filterDateQueryParamsString];
                const values = dateFilter.values;
                this.emitChange({ ...dateFilter.filterData, values });

                this.updateLabel$.next(this.getTypeLabelFromFilterDate(filterDate));
                this.inputWithDroppanelComponent.setDropdownClosed();
            });
    }

    private getFilterDateValueFromForm(): FilterDateValue {
        return this.filterTypeControl.value === FilterDateType.RANGE
            ? this.getRangeFilterDateValueFromForm()
            : this.getSpecificFilterDateValueFromForm();
    }

    private getRangeFilterDateValueFromForm(): FilterDateValue {
        const fromYearValue: number = this.filterRangeFromYearControl.value;
        const fromMonthValue: number | undefined = this.filterRangeFromMonthControl.value;
        const fromDayValue: number | undefined = this.filterRangeFromDayControl.value;
        const toYearValue: number = this.filterRangeToYearControl.value;
        const toMonthValue: number | undefined = this.filterRangeToMonthControl.value;
        const toDayValue: number | undefined = this.filterRangeToDayControl.value;

        return {
            Range: {
                ...(!!fromYearValue && {
                    From: {
                        Year: fromYearValue,
                        ...(!!fromMonthValue && { Month: fromMonthValue }),
                        ...(!!fromDayValue && { Day: fromDayValue })
                    }
                }),
                ...(!!toYearValue && {
                    To: {
                        Year: toYearValue,
                        ...(!!toMonthValue && { Month: toMonthValue }),
                        ...(!!toDayValue && { Day: toDayValue })
                    }
                })
            }
        };
    }

    private getSpecificFilterDateValueFromForm(): FilterDateValue {
        const yearValue: number = this.filterSpecificYearControl.value;
        const monthValue: number | undefined = this.filterSpecificMonthControl.value;
        const dayValue: number | undefined = this.filterSpecificDayControl.value;

        return {
            Specific: {
                Year: yearValue,
                ...(!!monthValue && { Month: monthValue }),
                ...(!!dayValue && { Day: dayValue })
            }
        };
    }

    private getTypeLabelFromFilterDate(filterDate: FilterDate): string {
        return filterDate.filterDateType === FilterDateType.RANGE
            ? this.filterLabels.range
            : this.filterLabels.specific;
    }

    isSelectedDateType(filterDateType: FilterDateType): boolean {
        return this.filterTypeControl.value === filterDateType;
    }

    private dateValidation(formGroup: UntypedFormGroup): { invalid: boolean } | null {
        this.clearError();

        const typeValue: FilterDateType = (formGroup.get('Type') as UntypedFormControl).value;
        const isValid: boolean =
            typeValue === FilterDateType.RANGE
                ? this.rangeDateValidation(formGroup)
                : this.specificDateValidation(formGroup);

        return isValid ? null : { invalid: true };
    }

    private specificDateValidation(formGroup: UntypedFormGroup): boolean {
        return this.hasCompleteDate(formGroup.get('Specific') as UntypedFormGroup);
    }

    private rangeDateValidation(formGroup: UntypedFormGroup): boolean {
        return (
            this.hasCompleteDate(formGroup.get('Range.From') as UntypedFormGroup) &&
            this.hasCompleteDate(formGroup.get('Range.To') as UntypedFormGroup) &&
            this.fromLesserThanToDateValidation(formGroup)
        );
    }

    /*
     *   Dates are considered incomplete when:
     *       Day field is set without Year and/or Month set
     *       Month field is set without Year set
     *
     *   Leaving all fields empty is considered valid
     */
    private hasCompleteDate(dateFormGroup: UntypedFormGroup): boolean {
        const hasYearValue = !!(dateFormGroup.get('Year') as UntypedFormControl).value;
        const hasMonthValue = !!(dateFormGroup.get('Month') as UntypedFormControl).value;
        const hasDayValue = !!(dateFormGroup.get('Day') as UntypedFormControl).value;

        const hasCompleteDate = !(
            (hasDayValue && (!hasYearValue || !hasMonthValue)) ||
            (hasMonthValue && !hasYearValue)
        );

        if (!hasCompleteDate) {
            this.setError(this.filterLabels.incompleteDate);
        }
        return hasCompleteDate;
    }

    /*
     *   In case of Range selection:
     *   From date needs to be smaller than To date
     *   Allow datesearch with wildcard left or right bounds, or wildcard in both directions
     */
    private fromLesserThanToDateValidation(formGroup: UntypedFormGroup): boolean {
        const fromYearValue: number | undefined = (formGroup.get('Range.From.Year') as UntypedFormControl).value;
        const fromMonthValue: number | undefined = (formGroup.get('Range.From.Month') as UntypedFormControl).value;
        const fromDayValue: number | undefined = (formGroup.get('Range.From.Day') as UntypedFormControl).value;

        const toYearValue: number | undefined = (formGroup.get('Range.To.Year') as UntypedFormControl).value;
        const toMonthValue: number | undefined = (formGroup.get('Range.To.Month') as UntypedFormControl).value;
        const toDayValue: number | undefined = (formGroup.get('Range.To.Day') as UntypedFormControl).value;

        const hasFromYearValue = !!fromYearValue;
        const hasToYearValue = !!toYearValue;

        const hasOnlyFromYearValue: boolean = hasFromYearValue && !hasToYearValue;
        const hasOnlyToYearValue: boolean = !hasFromYearValue && hasToYearValue;
        const isEmpty: boolean = !hasFromYearValue && !hasToYearValue;

        if (hasOnlyFromYearValue || hasOnlyToYearValue || isEmpty) {
            return true;
        }

        const fromDate: Date = new Date(fromYearValue || 1, fromMonthValue ? fromMonthValue - 1 : 0, fromDayValue || 1);
        fromDate.setFullYear(fromYearValue || 1);

        const toDate: Date = new Date(toYearValue || 1, toMonthValue ? toMonthValue - 1 : 0, toDayValue || 1);
        toDate.setFullYear(toYearValue || 1);

        if (isBefore(fromDate, toDate)) {
            return true;
        }

        this.setError(this.filterLabels.invalidRange);
        return false;
    }

    private setError(errorMessage: string): void {
        this.error$.next(errorMessage);
    }

    private clearError(): void {
        this.error$.next(null);
    }

    // #region FormControls
    get filterTypeControl(): UntypedFormControl {
        return this.form.get('Type') as UntypedFormControl;
    }

    get filterRangeControl(): UntypedFormControl {
        return this.form.get('Range') as UntypedFormControl;
    }

    get filterSpecificControl(): UntypedFormControl {
        return this.form.get('Specific') as UntypedFormControl;
    }

    get filterRangeFromControl(): UntypedFormControl {
        return this.filterRangeControl.get('From') as UntypedFormControl;
    }

    get filterRangeFromYearControl(): UntypedFormControl {
        return this.filterRangeControl.get('From.Year') as UntypedFormControl;
    }

    get filterRangeFromMonthControl(): UntypedFormControl {
        return this.filterRangeControl.get('From.Month') as UntypedFormControl;
    }

    get filterRangeFromDayControl(): UntypedFormControl {
        return this.filterRangeControl.get('From.Day') as UntypedFormControl;
    }

    get filterRangeToYearControl(): UntypedFormControl {
        return this.filterRangeControl.get('To.Year') as UntypedFormControl;
    }

    get filterRangeToControl(): UntypedFormControl {
        return this.filterRangeControl.get('To') as UntypedFormControl;
    }

    get filterRangeToMonthControl(): UntypedFormControl {
        return this.filterRangeControl.get('To.Month') as UntypedFormControl;
    }

    get filterRangeToDayControl(): UntypedFormControl {
        return this.filterRangeControl.get('To.Day') as UntypedFormControl;
    }

    get filterSpecificYearControl(): UntypedFormControl {
        return this.filterSpecificControl.get('Year') as UntypedFormControl;
    }

    get filterSpecificMonthControl(): UntypedFormControl {
        return this.filterSpecificControl.get('Month') as UntypedFormControl;
    }

    get filterSpecificDayControl(): UntypedFormControl {
        return this.filterSpecificControl.get('Day') as UntypedFormControl;
    }
    // #endregion
}
